import config from "../../../config/config";
import { authFetch, buildAuthHeaders } from "../../../public/api/httpClient";

const getApplicationsBase = () => {
  return `${config.BASE_URL}/membership-control/membership-applications`;
};


async function parseJson(res) {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return { message: text || "Invalid response" };
  }
}

/** User-facing copy: hide raw 5xx / DB details from the UI. */
function messageForResponse(res, payload, fallback) {
  if (res.status >= 500) {
    return "Internal server error. Please try again later.";
  }
  const m = payload?.message;
  return typeof m === "string" && m.trim() ? m : fallback;
}

/**
 * Fetch paginated membership applications (no filters)
 */
export const fetchMembershipApplications = async (params = {}) => {
  const queryParams = new URLSearchParams();

  if (params.page) queryParams.append("page", String(params.page));
  if (params.limit) queryParams.append("limit", String(params.limit));
  if (params.sortBy) queryParams.append("sortBy", params.sortBy);
  if (params.sortOrder) queryParams.append("sortOrder", params.sortOrder);

  const queryString = queryParams.toString();
  const url = `${getApplicationsBase()}${queryString ? `?${queryString}` : ""}`;

  const res = await authFetch(url, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await parseJson(res);

  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch applications")
    );
  }

  return {
    applications: data.data || [],
    meta: data.meta || { total: 0, page: 1, limit: 10, totalPages: 1 },
  };
};

/**
 * Fetch a single membership application by ID
 * @param {number} applicationId
 */
export const fetchApplicationById = async (applicationId) => {
  const res = await authFetch(
    `${getApplicationsBase()}/${encodeURIComponent(String(applicationId))}`,
    {
      credentials: "include",
      headers: buildAuthHeaders(),
    }
  );

  const data = await parseJson(res);

  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch application details")
    );
  }

  return data.data;
};

/**
 * Approve a membership application
 * @param {number} applicationId
 * @param {Object} body - Approval data
 * @param {string} body.remarks - Optional approval remarks
 * @param {string} csrfToken
 */
export const approveApplication = async (applicationId, body = {}, csrfToken) => {
  const res = await authFetch(
    `${getApplicationsBase()}/${encodeURIComponent(String(applicationId))}/approve`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(body),
    }
  );

  const data = await parseJson(res);

  if (!res.ok) {
    const error = new Error(
      messageForResponse(res, data, "Failed to approve application")
    );
    error.status = res.status;
    error.details = res.status >= 500 ? undefined : data.details;
    throw error;
  }

  return data;
};

/**
 * Reject a membership application
 * @param {number} applicationId
 * @param {Object} body - Rejection data
 * @param {string} body.reason - Required rejection reason
 * @param {string} csrfToken
 */
export const rejectApplication = async (applicationId, body = {}, csrfToken) => {
  const res = await authFetch(
    `${getApplicationsBase()}/${encodeURIComponent(String(applicationId))}/reject`,
    {
      method: "PUT",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(body),
    }
  );

  const data = await parseJson(res);

  if (!res.ok) {
    const error = new Error(
      messageForResponse(res, data, "Failed to reject application")
    );
    error.status = res.status;
    error.details = res.status >= 500 ? undefined : data.details;
    throw error;
  }

  return data;
};

export const verifyApplication = async (applicationId, body = {}, csrfToken) => {
  const res = await authFetch(
    `${getApplicationsBase()}/${encodeURIComponent(String(applicationId))}/verify`,
    {
      method: "PATCH",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
      body: JSON.stringify(body),
    }
  );

  const data = await parseJson(res);

  if (!res.ok) {
    const error = new Error(
      messageForResponse(res, data, "Failed to update application verification")
    );
    error.status = res.status;
    error.details = res.status >= 500 ? undefined : data.details;
    throw error;
  }

  return data;
};

export const deleteApplication = async (applicationId, csrfToken) => {
  const res = await authFetch(
    `${getApplicationsBase()}/${encodeURIComponent(String(applicationId))}`,
    {
      method: "DELETE",
      credentials: "include",
      headers: buildAuthHeaders(csrfToken ? { "X-CSRF-Token": csrfToken } : {}),
    }
  );
  const data = await parseJson(res);
  if (!res.ok) {
    const error = new Error(
      messageForResponse(res, data, "Failed to delete application")
    );
    error.status = res.status;
    error.details = res.status >= 500 ? undefined : data.details;
    throw error;
  }
  return data;
};

/**
 * Fetch dashboard statistics for applications
 */
export const fetchApplicationStats = async () => {
  const res = await authFetch(`${getApplicationsBase()}/dashboard`, {
    credentials: "include",
    headers: buildAuthHeaders(),
  });

  const data = await parseJson(res);

  if (!res.ok) {
    throw new Error(
      messageForResponse(res, data, "Failed to fetch dashboard stats")
    );
  }

  return data.data || {};
};
